import Vue from 'vue'
import JsonApiObject from '@/_models/JsonApi'

import StringTokenModel from '@/views/Language/models/stringToken'
import TranslationModel from '@/views/Language/models/translation'
import StringAttachmentModel from '@/views/Language/models/stringAttachment'

import ConfirmDelete from '@/views/Language/dialogs/confirmDelete/confirmDelete.vue'
import StringInput from '@/views/Language/dialogs/stringInput/stringInput.vue'
export default class StringModel extends JsonApiObject {
    static async post(data: any) {
        let query = {
            include: ['group', 'translations.textHistory', 'tokens', 'attachments'],
            body: { data: data },
        }

        let res = await Vue.prototype.$vbit.post('language', `strings`, query)

        return res.data ? new StringModel(res.data, res.included) : res
    }

    static async get(stringId: string) {
        let query = {
            include: ['group', 'translations.textHistory', 'tokens', 'attachments'],
        }

        let res = await Vue.prototype.$vbit.get('language', `strings/${stringId}`, query)

        return res.data ? new StringModel(res.data, res.included) : res
    }

    public constructor(data: any, included?) {
        super(data, included)
    }

    name
    description
    stringType
    deleted
    createdAt
    updatedAt

    get group() {
        return this._relationships['group'] ? this._relationships['group'] : null
    }

    get translations(): Array<TranslationModel> {
        return this._relationships['translations'] ? this._relationships['translations'] : []
    }

    set translations(translations: Array<TranslationModel>) {
        this._relationships['translations'] = translations
    }

    get missingTranslations(): Array<string> {
        return this._relationships['translations']
            .filter((translation) => !translation.text)
            .map((translation) => translation.language?._id)
    }

    get tokens(): Array<StringTokenModel> {
        return this._relationships['tokens'] ? this._relationships['tokens'] : []
    }

    set tokens(tokens: Array<StringTokenModel>) {
        this._relationships['tokens'] = tokens
    }

    get attachments(): Array<StringAttachmentModel> {
        return this._relationships['attachments'] ? this._relationships['attachments'] : []
    }
    set attachments(attachments: Array<StringAttachmentModel>) {
        this._relationships['attachments'] = attachments
    }

    async get() {
        let query = {
            include: ['translations.language', 'translations.textHistory', 'tokens', 'attachments'],
        }
        let res = await Vue.prototype.$vbit.get('language', `strings/${this._id}`, query)
        return this.initalize(res.data, res.included)
    }

    async delete() {
        let res = await JsonApiObject.openDialog(ConfirmDelete, {
            name: this.name,
            deleted: this.deleted,
            type: 'string',
            needConfirm: false,
        })
        if (res) {
            let data = {
                id: this._id,
                type: this._type,
                attributes: {
                    deleted: !this.deleted,
                },
            }

            return await this.patch(data)
        }
    }

    async initiatePatch(string: StringModel, strings: Array<StringModel>) {
        return await JsonApiObject.openDialog(StringInput, {
            string,
            strings,
        })
    }

    async patch(data: any) {
        let query = {
            updatedAt: this['updatedAt'],
            include: ['translations.language', 'tokens', 'attachments'],
            body: { data: data },
        }

        let res = await Vue.prototype.$vbit.patch('language', `strings/${this._id}`, query)

        return res.data ? this.initalize(res.data, res.included) : res
    }

    async postAttachment(fileId: string) {
        let query = {
            body: {
                data: {
                    type: 'stringAttachment',
                    attributes: {
                        fileId,
                    },
                    relationships: {
                        string: {
                            data: {
                                type: this._type,
                                id: this._id,
                            },
                        },
                    },
                },
            },
        }

        let res = await Vue.prototype.$vbit.post('language', `stringAttachments`, query)

        if (res.data) {
            let attachment = new StringAttachmentModel(res.data, res.included)
            this.attachments = [...this.attachments, attachment]
            return attachment
        } else {
            return res
        }
    }

    async postAttachments(attachments: Array<any>) {
        await Promise.all(
            attachments.map(async (attachment) => {
                let res = await this.postAttachment(attachment.id)
                attachments.push(res)
            })
        )
        await this.get()
    }

    async postToken(value: string, description: string | null) {
        if (this._id) {
            let token = await StringTokenModel.post(this._id, value, description)
            this.tokens = [...this.tokens, token]
            return token
        }
    }
}
